import { useState, useRef, useEffect, useCallback } from 'react'

interface AudioPlayerProps {
  audioUrl: string
  autoPlay?: boolean
  onPlay?: () => void
  onEnded?: () => void
}

export function AudioPlayer({ audioUrl, autoPlay = false, onPlay, onEnded }: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const play = useCallback(async () => {
    const audio = audioRef.current
    if (!audio) return

    try {
      setIsLoading(true)
      setError(null)
      await audio.play()
      setIsPlaying(true)
      onPlay?.()
    } catch (err: any) {
      console.error('Audio playback error:', err)
      setError('Failed to play audio')
    } finally {
      setIsLoading(false)
    }
  }, [onPlay])

  const pause = useCallback(() => {
    const audio = audioRef.current
    if (!audio) return

    audio.pause()
    setIsPlaying(false)
  }, [])

  const toggle = useCallback(() => {
    if (isPlaying) {
      pause()
    } else {
      play()
    }
  }, [isPlaying, play, pause])

  // Expose toggle function globally for keyboard shortcuts
  useEffect(() => {
    (window as any).toggleAudio = toggle
    return () => {
      delete (window as any).toggleAudio
    }
  }, [toggle])

  // Auto-play if enabled
  useEffect(() => {
    if (autoPlay && audioRef.current) {
      play()
    }
  }, [autoPlay, audioUrl, play])

  // Reset state when URL changes
  useEffect(() => {
    setIsPlaying(false)
    setError(null)
  }, [audioUrl])

  const handleEnded = () => {
    setIsPlaying(false)
    onEnded?.()
  }

  const handleError = () => {
    setIsPlaying(false)
    setIsLoading(false)
    setError('Audio not available')
  }

  return (
    <div className="flex items-center gap-2">
      <audio
        ref={audioRef}
        src={audioUrl}
        onEnded={handleEnded}
        onError={handleError}
        preload="metadata"
      />

      <button
        onClick={toggle}
        disabled={isLoading}
        className={`
          inline-flex items-center justify-center
          w-10 h-10 rounded-full
          transition-colors duration-150
          ${error
            ? 'bg-red-100 text-red-600 cursor-not-allowed'
            : isPlaying
              ? 'bg-indigo-600 text-white hover:bg-indigo-700'
              : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
          }
          ${isLoading ? 'opacity-50 cursor-wait' : ''}
        `}
        title={error || (isPlaying ? 'Pause (P)' : 'Play (P)')}
        aria-label={isPlaying ? 'Pause audio' : 'Play audio'}
      >
        {isLoading ? (
          <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        ) : error ? (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        ) : isPlaying ? (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <rect x="6" y="4" width="4" height="16" rx="1" />
            <rect x="14" y="4" width="4" height="16" rx="1" />
          </svg>
        ) : (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M8 5v14l11-7z" />
          </svg>
        )}
      </button>

      {error && (
        <span className="text-xs text-red-600">{error}</span>
      )}
    </div>
  )
}
